(function () {
  const MAX_ATTEMPTS = 5;
  const VISITOR_ID_KEY = 'ddAiPreviewVisitorId';
  const ANALYTICS_VERSION = '2';
  const GA4_MEASUREMENT_ID = 'G-WN0SELMF2M';
  const CONFIG_CACHE = new Map();
  const LOADING_STEPS = [
    {
      title: 'Reading your pet photo',
      copy: 'Finding the face, ears, muzzle, and markings that make your pet recognizable.',
      status: 'Reading your pet photo...',
      progress: 16
    },
    {
      title: 'Finding the face details',
      copy: 'Checking the photo before we draw so the preview stays close to your pet.',
      status: 'Finding your pet’s face details...',
      progress: 30
    },
    {
      title: 'Drawing custom line art',
      copy: 'Our AI artist is sketching a clean portrait from your uploaded photo.',
      status: 'Drawing your custom pet line art...',
      progress: 52
    },
    {
      title: 'Placing it on your product',
      copy: 'Positioning the artwork on this item and print placement.',
      status: 'Placing the artwork on this product...',
      progress: 70
    },
    {
      title: 'Checking the artwork',
      copy: 'Still working — detailed fur, darker photos, or close crops can take a little longer.',
      status: 'Still working. Checking the artwork before we show it...',
      progress: 84
    },
    {
      title: 'Almost there',
      copy: 'Final quality check. Please keep this page open while we prepare your preview.',
      status: 'Almost there. Preparing your preview...',
      progress: 93
    }
  ];

  function analyticsProcessingAllowed() {
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      return !privacy ||
        typeof privacy.analyticsProcessingAllowed !== 'function' ||
        privacy.analyticsProcessingAllowed();
    } catch (error) {
      return false;
    }
  }

  function ensureGa4() {
    if (!analyticsProcessingAllowed()) return false;
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
    }
    if (!window.ddAiPreviewGa4Initialized) {
      window.ddAiPreviewGa4Initialized = true;
      window.gtag('js', new Date());
      window.gtag('config', GA4_MEASUREMENT_ID, { send_page_view: false });
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA4_MEASUREMENT_ID)}`;
      script.setAttribute('data-dd-ai-preview-ga4', 'true');
      document.head.appendChild(script);
    }
    return true;
  }

  function track(eventName, detail) {
    const payload = Object.assign({
      event_category: 'ai_preview',
      funnel_version: ANALYTICS_VERSION
    }, detail || {});
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(eventName, payload);
      }
      if (ensureGa4()) {
        window.gtag('event', eventName, Object.assign({
          send_to: GA4_MEASUREMENT_ID
        }, payload));
      }
    } catch (error) {
      console.warn('Dilly AI preview analytics emit failed', error);
    }
    document.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
  }

  function fileSizeBucket(bytes) {
    if (bytes < 1024 * 1024) return 'under_1mb';
    if (bytes < 4 * 1024 * 1024) return '1_to_4mb';
    if (bytes <= 8 * 1024 * 1024) return '4_to_8mb';
    return 'over_8mb';
  }

  function generationFailureType(error) {
    const message = String(error && error.message || error || '').toLowerCase();
    if (message.includes('429') || message.includes('rate limit') || message.includes('preview limit')) return 'rate_limited';
    if (message.includes('taking longer') || message.includes('timeout') || message.includes('aborted')) return 'timeout';
    if (message.includes('form was not ready')) return 'product_form_unavailable';
    if (
      message.includes('clear photo') ||
      message.includes('single pet') ||
      message.includes('not a pet') ||
      message.includes('pet photo') ||
      message.includes('face visible')
    ) return 'photo_rejected';
    if (
      message.includes('did not respond') ||
      message.includes('load failed') ||
      message.includes('failed to fetch') ||
      message.includes('network')
    ) return 'network_error';
    if (
      message.includes('temporarily unavailable') ||
      message.includes('returned 500') ||
      message.includes('returned 502') ||
      message.includes('returned 503') ||
      message.includes('returned 504')
    ) return 'service_unavailable';
    return 'unknown';
  }

  function getVisitorId() {
    try {
      let visitorId = window.localStorage.getItem(VISITOR_ID_KEY);
      if (!visitorId) {
        visitorId = window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
        window.localStorage.setItem(VISITOR_ID_KEY, visitorId);
      }
      return visitorId;
    } catch (error) {
      return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    }
  }

  function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(reader.error || new Error('Could not read file'));
      reader.readAsDataURL(file);
    });
  }

  function previewServiceBaseUrl(endpoint) {
    return String(endpoint || '').replace(/\/+(preview|api\/dilly\/preview)?\/?$/, '');
  }

  function previewApiUrl(endpoint) {
    return `${previewServiceBaseUrl(endpoint)}/preview`;
  }

  function previewUnlockApiUrl(endpoint) {
    return `${previewServiceBaseUrl(endpoint)}/preview-unlock`;
  }

  function warmPreviewService(endpoint) {
    const healthUrl = `${previewServiceBaseUrl(endpoint)}/health`;
    fetch(healthUrl, { method: 'GET', mode: 'cors', cache: 'no-store' }).catch(() => {});
  }

  async function requestPreview(endpoint, payload) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 210000);
    let response;
    try {
      response = await fetch(previewApiUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal
      });
    } catch (error) {
      if (error && error.name === 'AbortError') {
        throw new Error(
          'The instant preview is taking longer than expected. You can still upload your photo for an artist proof.'
        );
      }
      throw new Error(
        error && String(error.message || error) !== 'Load failed'
          ? String(error.message || error)
          : 'The instant preview could not finish. Try another clear front-facing photo, or continue with your artist proof.'
      );
    } finally {
      window.clearTimeout(timeout);
    }

    const responseText = await response.text();
    let result = {};
    try {
      result = responseText ? JSON.parse(responseText) : {};
    } catch (error) {
      result = {};
    }
    if (response.ok && result.ok) return result;
    throw new Error(
      result.error ||
      (response.status >= 500
        ? 'The instant preview is temporarily unavailable. You can still upload your photo for an artist proof.'
        : `Preview service returned ${response.status}`)
    );
  }

  async function requestPreviewUnlock(endpoint, payload) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(previewUnlockApiUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal
      });
      const responseText = await response.text();
      let result = {};
      try {
        result = responseText ? JSON.parse(responseText) : {};
      } catch (error) {
        result = {};
      }
      if (response.ok && result.ok) return result;
      throw new Error(result.error || `Preview unlock returned ${response.status}`);
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function emailLooksValid(email) {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(email || '').trim());
  }

  function utmFields() {
    const out = {};
    try {
      const params = new URLSearchParams(window.location.search || '');
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((key) => {
        const value = params.get(key);
        if (value) out[key] = value.slice(0, 220);
      });
    } catch (error) {}
    return out;
  }

  function previewUnlockPayload(module, result, email, consentText) {
    const placement = currentPlacement(module);
    return Object.assign({
      email,
      marketing_consent: true,
      consent_text: consentText,
      job_id: result.job_id || '',
      visitor_id: getVisitorId(),
      product_handle: module.dataset.productHandle || '',
      product_title: module.dataset.productTitle || '',
      variant_id: getVariantId(module),
      print_location: result.print_location_label || (placement && placement.label) || '',
      ai_preview_url: result.ai_preview_url || '',
      pet_photo_url: result.pet_photo_url || result.dog_photo_url || '',
      page_url: window.location.href
    }, utmFields());
  }

  function normalizeId(value) {
    return String(value || '').split('/').pop();
  }

  async function loadConfig(url) {
    if (!url) return null;
    if (!CONFIG_CACHE.has(url)) {
      CONFIG_CACHE.set(url, fetch(url, { credentials: 'same-origin' }).then((response) => {
        if (!response.ok) throw new Error('Preview product configuration could not be loaded');
        return response.json();
      }));
    }
    return CONFIG_CACHE.get(url);
  }

  function getProductForm(module) {
    const scope = module.closest('product-info') || document;
    const forms = Array.from(scope.querySelectorAll('form[action*="/cart/add"]'));
    return forms.find((form) => form.querySelector('button[name="add"], [type="submit"][name="add"]')) ||
      scope.querySelector('product-form form[action*="/cart/add"]') ||
      forms[0] ||
      document.querySelector('product-form form[action*="/cart/add"], form[action*="/cart/add"]');
  }

  function getVariantId(module) {
    const form = getProductForm(module);
    const input = form && form.querySelector('[name="id"]');
    return normalizeId(input ? input.value : module.dataset.variantId);
  }

  function productConfig(module) {
    const all = module.ddAiPreviewConfig;
    return all && all.products && all.products[module.dataset.productHandle];
  }

  function variantConfig(module) {
    const product = productConfig(module);
    if (!product) return null;
    const variantId = getVariantId(module);
    return (product.variants && product.variants[variantId]) ||
      (product.variants && product.variants[String(product.default_variant_id || '')]) ||
      null;
  }

  function placements(module) {
    const product = productConfig(module);
    return product && Array.isArray(product.placements) && product.placements.length
      ? product.placements
      : [];
  }

  function currentPlacement(module) {
    const checked = module.querySelector('[data-dd-preview-placement-input]:checked');
    const available = placements(module);
    if (checked) {
      const found = available.find((placement) => placement.key === checked.value || placement.label === checked.value);
      if (found) return found;
    }
    return available[0] || null;
  }

  function currentMockupUrl(module) {
    const variant = variantConfig(module);
    const product = productConfig(module);
    return (variant && variant.mockup_url) || (product && product.default_mockup_url) || '';
  }

  function renderPlacementChoices(module, keepKey) {
    const fieldset = module.querySelector('[data-dd-preview-placement]');
    const container = module.querySelector('[data-dd-preview-placement-options]');
    const available = placements(module);
    if (!fieldset || !container || !available.length) return;
    fieldset.hidden = available.length <= 1;
    const selectedKey = keepKey && available.some((placement) => placement.key === keepKey)
      ? keepKey
      : available[0].key;
    container.innerHTML = available.map((placement, index) => `
      <label class="dd-ai-preview__placement-choice">
        <input
          type="radio"
          name="dd-ai-preview-placement-${module.dataset.sectionId || 'main'}"
          value="${placement.key}"
          data-dd-preview-placement-input
          ${placement.key === selectedKey || (!selectedKey && index === 0) ? 'checked' : ''}
        >
        <span>${placement.label}<small>${placement.help || 'Preview placement'}</small></span>
      </label>
    `).join('');
  }

  function syncGpoPrintLocation(module, placement, attempt) {
    if (!placement || !placement.label) return false;
    const scope = module.closest('product-info') || document;
    const input = Array.from(scope.querySelectorAll('input[name="radio-1"]'))
      .find((candidate) => candidate.value === placement.label);
    if (input) {
      if (!input.checked) input.click();
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    if ((attempt || 0) < 10) {
      window.setTimeout(() => syncGpoPrintLocation(module, placement, (attempt || 0) + 1), 350);
    }
    return false;
  }

  function syncGpoFieldBypass(module, attempt, options) {
    const bypassPhoto = Boolean(options && options.photo);
    const hidePhoto = Boolean(options && options.hidePhoto);
    const bypassPlacement = !options || options.placement !== false;
    const scope = module.closest('product-info') || document;
    const fileInput = scope.querySelector('input.gpo-file[name="Pet-image"], input#file-1[name="Pet-image"]');
    if (fileInput) {
      const field = fileInput.closest('.gpo-element') || fileInput.parentElement;
      const wasBypassed = fileInput.dataset.ddAiPreviewBypassed === 'true';
      if (!('ddAiPreviewHadPresence' in fileInput.dataset)) {
        fileInput.dataset.ddAiPreviewHadPresence = fileInput.hasAttribute('presence') ? 'true' : 'false';
        fileInput.dataset.ddAiPreviewWasDisabled = fileInput.disabled ? 'true' : 'false';
      }

      if (bypassPhoto) {
        fileInput.dataset.ddAiPreviewBypassed = 'true';
        fileInput.removeAttribute('presence');
        fileInput.required = false;
        fileInput.disabled = true;
        if (field) {
          field.classList.remove('has-error');
          field.querySelectorAll('.messages .help-block.error').forEach((error) => error.remove());
        }
      } else if (wasBypassed) {
        delete fileInput.dataset.ddAiPreviewBypassed;
        if (fileInput.dataset.ddAiPreviewHadPresence === 'true') {
          fileInput.setAttribute('presence', '');
        }
        fileInput.disabled = fileInput.dataset.ddAiPreviewWasDisabled === 'true';
      }

      if (field && (hidePhoto || bypassPhoto)) {
        field.classList.add('dd-ai-preview__gpo-photo-hidden');
        field.setAttribute('aria-hidden', 'true');
      } else if (field) {
        field.classList.remove('dd-ai-preview__gpo-photo-hidden');
        field.removeAttribute('aria-hidden');
      }

      if (wasBypassed !== bypassPhoto) {
        // Globo caches its validation constraints. A change event forces it to
        // rebuild them after the custom `presence` requirement changes.
        fileInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    const printInput = scope.querySelector('input[name="radio-1"][value="Left Chest"], input[name="radio-1"][value="Big Center Print"]');
    if (printInput) {
      const field = printInput.closest('.gpo-element') || printInput.parentElement;
      if (field && bypassPlacement) {
        field.classList.add('dd-ai-preview__gpo-placement-bypassed');
        field.setAttribute('aria-hidden', 'true');
      } else if (field) {
        field.classList.remove('dd-ai-preview__gpo-placement-bypassed');
        field.removeAttribute('aria-hidden');
      }
    }

    if ((bypassPhoto || hidePhoto || bypassPlacement) && !fileInput && (attempt || 0) < 12) {
      window.setTimeout(() => syncGpoFieldBypass(module, (attempt || 0) + 1, options), 350);
    }
  }

  function syncAiPlacementFromGpo(module, value) {
    if (!value || value === 'N/A') return;
    const placement = placements(module).find((item) => item.label === value || item.key === value);
    if (!placement) return;
    const input = Array.from(module.querySelectorAll('[data-dd-preview-placement-input]'))
      .find((candidate) => candidate.value === placement.key);
    if (!input || input.checked) return;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function repairGpoRadioClicks(module, scope) {
    scope.addEventListener('click', (event) => {
      const target = event.target;
      if (!target || !target.closest) return;
      const choice = target.closest('.gpo-choicelist');
      if (!choice || !scope.contains(choice)) return;
      const input = choice.querySelector('input[type="radio"][name="radio-1"]');
      if (!input || input.disabled) return;
      const applyChoice = () => {
        input.checked = true;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        syncAiPlacementFromGpo(module, input.value);
      };
      applyChoice();
      window.setTimeout(applyChoice, 0);
      window.setTimeout(applyChoice, 120);
    }, { capture: true });
  }

  function setLineProperty(form, name, value) {
    let input = form.querySelector(`input[name="properties[${name}]"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.setAttribute('data-dd-ai-preview-property', 'true');
      form.appendChild(input);
    }
    input.value = value || '';
  }

  function clearLineProperties(module) {
    const form = getProductForm(module);
    if (!form) return;
    form.querySelectorAll('[data-dd-ai-preview-property]').forEach((input) => input.remove());
  }

  function attachProperties(module, result) {
    const form = getProductForm(module);
    if (!form) return false;
    const placement = currentPlacement(module);
    setLineProperty(form, 'Reference Photo', result.pet_photo_url || result.dog_photo_url || '');
    setLineProperty(form, 'AI Preview', result.ai_preview_url || '');
    setLineProperty(form, '_Preview Job ID', result.job_id || '');
    setLineProperty(form, '_Preview Status', result.status || 'complete');
    setLineProperty(form, '_Preview Print Location', result.print_location_label || (placement && placement.label) || '');
    setLineProperty(form, '_Preview Product Mockup', currentMockupUrl(module));
    setLineProperty(form, '_Product Handle', module.dataset.productHandle || '');
    setLineProperty(form, '_Variant ID', getVariantId(module));
    syncGpoPrintLocation(module, placement);
    syncGpoFieldBypass(module, 0, { photo: true, placement: true });
    return true;
  }

  function showError(module, message) {
    hideLoadingPanel(module);
    module.querySelector('[data-dd-preview-error]').textContent = message;
    module.querySelector('[data-dd-preview-status]').textContent = '';
  }

  function clearError(module) {
    module.querySelector('[data-dd-preview-error]').textContent = '';
  }

  function loadingElements(module) {
    return {
      panel: module.querySelector('[data-dd-preview-loader]'),
      title: module.querySelector('[data-dd-preview-loader-title]'),
      copy: module.querySelector('[data-dd-preview-loader-copy]'),
      progress: module.querySelector('[data-dd-preview-loader-progress]'),
      steps: Array.from(module.querySelectorAll('[data-dd-preview-loader-step]')),
      note: module.querySelector('[data-dd-preview-loader-note]'),
      status: module.querySelector('[data-dd-preview-status]')
    };
  }

  function setLoadingStep(module, index) {
    const loader = loadingElements(module);
    const stepIndex = Math.max(0, Math.min(index, LOADING_STEPS.length - 1));
    const step = LOADING_STEPS[stepIndex];
    if (loader.title) loader.title.textContent = step.title;
    if (loader.copy) loader.copy.textContent = step.copy;
    if (loader.status) loader.status.textContent = step.status;
    if (loader.progress) loader.progress.style.width = `${step.progress}%`;
    loader.steps.forEach((item, itemIndex) => {
      item.classList.toggle('is-complete', itemIndex < Math.min(stepIndex, 4));
      item.classList.toggle('is-active', itemIndex === Math.min(stepIndex, 4));
    });
    if (loader.note) {
      loader.note.textContent = stepIndex >= 4
        ? 'Some previews take a little longer because we check the artwork before showing it.'
        : 'Custom previews usually take 30–60 seconds. Please keep this page open.';
    }
  }

  function loadingStepForElapsed(elapsedMs) {
    if (elapsedMs >= 50000) return 5;
    if (elapsedMs >= 30000) return 4;
    if (elapsedMs >= 18000) return 3;
    if (elapsedMs >= 9000) return 2;
    if (elapsedMs >= 4000) return 1;
    return 0;
  }

  function showLoadingPanel(module) {
    const loader = loadingElements(module);
    if (!loader.panel) return;
    loader.panel.hidden = false;
    setLoadingStep(module, 0);
    safeScrollIntoView(loader.panel);
  }

  function hideLoadingPanel(module) {
    const loader = loadingElements(module);
    if (loader.panel) loader.panel.hidden = true;
    if (loader.progress) loader.progress.style.width = '14%';
    loader.steps.forEach((item) => {
      item.classList.remove('is-active', 'is-complete');
    });
  }

  function setBusy(module, busy) {
    module.querySelectorAll('button').forEach((button) => {
      if (button.hasAttribute('data-dd-preview-replace')) return;
      button.disabled = busy;
    });
    const generateButton = module.querySelector('[data-dd-preview-generate]');
    if (generateButton) {
      if (!generateButton.dataset.idleText) generateButton.dataset.idleText = generateButton.textContent.trim();
      generateButton.textContent = busy ? 'Creating Preview…' : generateButton.dataset.idleText;
    }
    module.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function safeScrollIntoView(element) {
    if (!element || !element.scrollIntoView) return;
    window.setTimeout(() => {
      element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }, 120);
  }

  function gateElements(module) {
    return {
      gate: module.querySelector('[data-dd-preview-gate]'),
      email: module.querySelector('[data-dd-preview-email]'),
      consent: module.querySelector('[data-dd-preview-consent]'),
      unlock: module.querySelector('[data-dd-preview-unlock]'),
      skip: module.querySelector('[data-dd-preview-skip-email]'),
      error: module.querySelector('[data-dd-preview-gate-error]')
    };
  }

  function setGateBusy(module, busy) {
    const gate = gateElements(module);
    [gate.unlock, gate.skip, gate.email, gate.consent].forEach((item) => {
      if (item) item.disabled = busy;
    });
  }

  function hidePreviewGate(module) {
    const gate = gateElements(module);
    if (gate.gate) gate.gate.hidden = true;
    if (gate.error) gate.error.textContent = '';
    setGateBusy(module, false);
  }

  function showPreviewGate(module, result) {
    const gate = gateElements(module);
    hideLoadingPanel(module);
    module.ddAiPreviewPendingResult = result;
    module.ddAiPreviewLatestResult = null;
    if (gate.gate) gate.gate.hidden = false;
    if (gate.error) gate.error.textContent = '';
    setGateBusy(module, false);
    track('dd_ai_preview_gate_shown', {
      product_handle: module.dataset.productHandle,
      variant_id: getVariantId(module),
      job_id: result.job_id || '',
      print_location: result.print_location_label || (currentPlacement(module) && currentPlacement(module).label)
    });
    safeScrollIntoView(gate.gate);
  }

  function revealGeneratedPreview(module, result, options) {
    const placement = currentPlacement(module);
    const previewImage = module.querySelector('[data-dd-preview-result]');
    const previewCaption = module.querySelector('[data-dd-preview-result-caption]');
    const previewFigure = module.querySelector('[data-dd-preview-result-figure]');
    const status = module.querySelector('[data-dd-preview-status]');
    const accepted = module.querySelector('[data-dd-preview-accepted]');
    const manualButton = module.querySelector('[data-dd-preview-manual]');
    module.ddAiPreviewLatestResult = result;
    module.ddAiPreviewPendingResult = null;
    const attached = attachProperties(module, result);
    if (!attached) {
      throw new Error('Preview created, but the product form was not ready. Please refresh and try again.');
    }
    if (previewImage) previewImage.src = result.ai_preview_url;
    if (previewCaption) previewCaption.textContent = `Line art on this item: ${result.print_location_label || (placement && placement.label) || 'Preview'}`;
    if (previewFigure) previewFigure.hidden = false;
    if (status) status.textContent = 'Preview ready and attached to your order.';
    if (accepted) accepted.textContent = 'Your pet photo and AI preview will be included for artist reference.';
    if (manualButton) manualButton.hidden = true;
    hideLoadingPanel(module);
    hidePreviewGate(module);
    safeScrollIntoView(previewFigure || accepted);
    return true;
  }

  function clearGeneratedPreview(module, message) {
    module.ddAiPreviewLatestResult = null;
    module.ddAiPreviewPendingResult = null;
    module.ddAiPreviewManualUpload = false;
    clearLineProperties(module);
    syncGpoFieldBypass(module, 0, { photo: false, hidePhoto: true, placement: true });
    const previewImage = module.querySelector('[data-dd-preview-result]');
    const previewFigure = module.querySelector('[data-dd-preview-result-figure]');
    const accepted = module.querySelector('[data-dd-preview-accepted]');
    const manualButton = module.querySelector('[data-dd-preview-manual]');
    if (previewImage) previewImage.removeAttribute('src');
    if (previewFigure) previewFigure.hidden = true;
    if (accepted) accepted.textContent = '';
    if (manualButton) manualButton.hidden = false;
    hideLoadingPanel(module);
    hidePreviewGate(module);
    if (message) module.querySelector('[data-dd-preview-status]').textContent = message;
  }

  function refreshVariantContext(module, messageIfChanged) {
    const previousVariant = module.ddAiPreviewVariantId;
    const nextVariant = getVariantId(module);
    module.ddAiPreviewVariantId = nextVariant;
    const mockupUrl = currentMockupUrl(module);
    module.dataset.mockupUrl = mockupUrl;
    const placement = currentPlacement(module);
    syncGpoPrintLocation(module, placement);
    if (previousVariant && previousVariant !== nextVariant && (module.ddAiPreviewLatestResult || module.ddAiPreviewPendingResult)) {
      clearGeneratedPreview(module, messageIfChanged || 'Product option changed. Generate a fresh preview for this selection.');
    }
  }

  async function initModule(module) {
    if (module.dataset.ddAiPreviewInitialized === 'true') return;
    module.dataset.ddAiPreviewInitialized = 'true';

    const endpoint = module.dataset.endpoint;
    if (!endpoint) {
      module.hidden = true;
      return;
    }
    try {
      module.ddAiPreviewConfig = await loadConfig(module.dataset.configUrl);
    } catch (error) {
      module.hidden = true;
      return;
    }
    const config = productConfig(module);
    if (!config || !config.default_mockup_url || !placements(module).length) {
      module.hidden = true;
      return;
    }

    module.hidden = false;
    warmPreviewService(endpoint);
    module.ddAiPreviewLatestResult = null;
    module.ddAiPreviewPendingResult = null;
    module.ddAiPreviewManualUpload = false;
    module.ddAiPreviewVariantId = getVariantId(module);
    renderPlacementChoices(module);
    refreshVariantContext(module);
    syncGpoFieldBypass(module, 0, { photo: false, hidePhoto: true, placement: true });

    const fileInput = module.querySelector('[data-dd-preview-file]');
    const dropZone = module.querySelector('[data-dd-preview-drop]');
    const generateButton = module.querySelector('[data-dd-preview-generate]');
    const manualButton = module.querySelector('[data-dd-preview-manual]');
    const replaceButtons = module.querySelectorAll('[data-dd-preview-replace]');
    const originalImage = module.querySelector('[data-dd-preview-original]');
    const previewImage = module.querySelector('[data-dd-preview-result]');
    const previewCaption = module.querySelector('[data-dd-preview-result-caption]');
    const previewFigure = module.querySelector('[data-dd-preview-result-figure]');
    const status = module.querySelector('[data-dd-preview-status]');
    const resultPanel = module.querySelector('[data-dd-preview-panel]');
    const accepted = module.querySelector('[data-dd-preview-accepted]');
    const gate = gateElements(module);
    const consentText = 'I agree to receive emails from Dilly Dally Dog about my preview and custom pet products.';

    let selectedFile = null;
    let selectedDataUrl = '';
    let attempts = Number(sessionStorage.getItem('ddAiPreviewAttempts') || '0');

    fileInput.addEventListener('change', async () => {
      clearError(module);
      clearGeneratedPreview(module);
      hideLoadingPanel(module);
      selectedDataUrl = '';
      originalImage.removeAttribute('src');
      resultPanel.hidden = true;
      generateButton.disabled = true;
      selectedFile = fileInput.files && fileInput.files[0];
      if (!selectedFile) return;
      track('dd_ai_preview_photo_selected', {
        product_handle: module.dataset.productHandle,
        variant_id: getVariantId(module),
        file_type: selectedFile.type || 'unknown',
        file_size_bucket: fileSizeBucket(selectedFile.size),
        selection_status: selectedFile.size > 8 * 1024 * 1024 ? 'rejected_too_large' : 'accepted'
      });
      if (selectedFile.size > 8 * 1024 * 1024) {
        showError(module, 'That photo is too large. Please upload an image under 8MB.');
        selectedFile = null;
        return;
      }
      selectedDataUrl = await fileToDataUrl(selectedFile);
      originalImage.src = selectedDataUrl;
      resultPanel.hidden = false;
      generateButton.disabled = false;
      status.textContent = 'Photo ready. Generate your preview when you are ready.';
      safeScrollIntoView(generateButton);
    });

    if (dropZone) {
      dropZone.addEventListener('dragover', (event) => {
        event.preventDefault();
      });
      dropZone.addEventListener('drop', async (event) => {
        event.preventDefault();
        if (!event.dataTransfer || !event.dataTransfer.files || !event.dataTransfer.files.length) return;
        const transfer = new DataTransfer();
        transfer.items.add(event.dataTransfer.files[0]);
        fileInput.files = transfer.files;
        fileInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    module.addEventListener('change', (event) => {
      const target = event.target;
      if (target && target.matches && target.matches('[data-dd-preview-placement-input]')) {
        const placement = currentPlacement(module);
        syncGpoPrintLocation(module, placement);
        if (module.ddAiPreviewLatestResult || module.ddAiPreviewPendingResult) {
          clearGeneratedPreview(module, 'Placement changed. Generate a fresh preview for this print location.');
        }
      }
    });

    module.addEventListener('click', (event) => {
      const choice = event.target && event.target.closest && event.target.closest('.dd-ai-preview__placement-choice');
      if (!choice || !module.contains(choice)) return;
      const input = choice.querySelector('[data-dd-preview-placement-input]');
      if (!input || input.checked) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    const productScope = module.closest('product-info') || document;
    repairGpoRadioClicks(module, productScope);
    productScope.addEventListener('change', (event) => {
      const target = event.target;
      if (target && target.matches && target.matches('input[name="radio-1"]')) return;
      window.setTimeout(() => refreshVariantContext(module), 0);
    });
    productScope.addEventListener('variant:change', () => refreshVariantContext(module));

    generateButton.addEventListener('click', async () => {
      clearError(module);
      accepted.textContent = '';
      refreshVariantContext(module);
      const placement = currentPlacement(module);
      const mockupUrl = currentMockupUrl(module);
      if (!mockupUrl) {
        showError(module, 'This product selection is missing a preview mockup. Please choose another option or order normally for an artist proof.');
        return;
      }
      if (!selectedFile || !selectedDataUrl) {
        fileInput.click();
        return;
      }
      if (attempts >= MAX_ATTEMPTS) {
        showError(module, 'Preview limit reached for this visit. You can still order and our artists will create your proof.');
        return;
      }
      const attemptNumber = attempts + 1;
      setBusy(module, true);
      showLoadingPanel(module);
      track('dd_ai_preview_generation_started', {
        product_handle: module.dataset.productHandle,
        variant_id: getVariantId(module),
        attempt_number: attemptNumber,
        print_location: placement && placement.label
      });
      const loadingStartedAt = Date.now();
      const statusTimer = window.setInterval(() => {
        setLoadingStep(module, loadingStepForElapsed(Date.now() - loadingStartedAt));
      }, 3000);
      setLoadingStep(module, 0);

      try {
        const result = await requestPreview(endpoint, {
          filename: selectedFile.name,
          mime_type: selectedFile.type || 'image/jpeg',
          image_base64: selectedDataUrl,
          product_handle: module.dataset.productHandle,
          product_title: module.dataset.productTitle,
          variant_id: getVariantId(module),
          mockup_url: mockupUrl,
          print_location: placement && placement.label,
          placement_config: placement,
          visitor_id: getVisitorId()
        });
        attempts = attemptNumber;
        sessionStorage.setItem('ddAiPreviewAttempts', String(attempts));
        previewImage.removeAttribute('src');
        if (previewFigure) previewFigure.hidden = true;
        hideLoadingPanel(module);
        status.textContent = 'Your free preview is ready. Unlock it below to see it.';
        accepted.textContent = '';
        manualButton.hidden = true;
        showPreviewGate(module, result);
        track('dd_ai_preview_generation_completed', {
          product_handle: module.dataset.productHandle,
          variant_id: getVariantId(module),
          elapsed_ms: result.elapsed_ms,
          print_location: result.print_location_label || (placement && placement.label),
          model: result.model || 'unknown'
        });
      } catch (error) {
        module.ddAiPreviewLatestResult = null;
        module.ddAiPreviewManualUpload = true;
        clearLineProperties(module);
        syncGpoFieldBypass(module, 0, { photo: false, hidePhoto: false, placement: true });
        manualButton.hidden = true;
        previewImage.removeAttribute('src');
        if (previewFigure) previewFigure.hidden = true;
        hideLoadingPanel(module);
        const message = String(error && error.message || error || '');
        showError(module, message && message !== 'Preview generation failed'
          ? message
          : 'We could not generate a preview from this photo, but you can still order and our artists will create your proof.');
        track('dd_ai_preview_generation_failed', {
          product_handle: module.dataset.productHandle,
          variant_id: getVariantId(module),
          attempt_number: attemptNumber,
          print_location: placement && placement.label,
          failure_type: generationFailureType(error)
        });
      } finally {
        window.clearInterval(statusTimer);
        setBusy(module, false);
      }
    });

    replaceButtons.forEach((button) => button.addEventListener('click', () => {
      fileInput.value = '';
      fileInput.click();
    }));

    if (gate.unlock) {
      gate.unlock.addEventListener('click', async () => {
        const result = module.ddAiPreviewPendingResult;
        if (!result) return;
        const email = gate.email ? String(gate.email.value || '').trim().toLowerCase() : '';
        if (!emailLooksValid(email)) {
          if (gate.error) gate.error.textContent = 'Please enter a valid email address.';
          if (gate.email) gate.email.focus();
          return;
        }
        if (!gate.consent || !gate.consent.checked) {
          if (gate.error) gate.error.textContent = 'Please check the email permission box, or use the no-thanks link below.';
          if (gate.consent) gate.consent.focus();
          return;
        }
        if (gate.error) gate.error.textContent = '';
        requestPreviewUnlock(endpoint, previewUnlockPayload(module, result, email, consentText)).catch((error) => {
          console.warn('Dilly AI preview email sync failed; revealing preview anyway', error);
        });
        track('dd_ai_preview_email_submitted', {
          product_handle: module.dataset.productHandle,
          variant_id: getVariantId(module),
          job_id: result.job_id || '',
          print_location: result.print_location_label || (currentPlacement(module) && currentPlacement(module).label)
        });
        revealGeneratedPreview(module, result, { source: 'email_submit' });
      });
    }

    if (gate.skip) {
      gate.skip.addEventListener('click', () => {
        const result = module.ddAiPreviewPendingResult;
        if (!result) return;
        track('dd_ai_preview_email_skipped', {
          product_handle: module.dataset.productHandle,
          variant_id: getVariantId(module),
          job_id: result.job_id || '',
          source: 'gate_skip'
        });
        revealGeneratedPreview(module, result, { source: 'gate_skip' });
      });
    }

    manualButton.addEventListener('click', () => {
      module.ddAiPreviewManualUpload = true;
      manualButton.hidden = true;
      hidePreviewGate(module);
      syncGpoFieldBypass(module, 0, { photo: false, hidePhoto: false, placement: true });
      status.textContent = 'Upload your pet photo below, then add the item to your cart.';
      const gpoFile = productScope.querySelector('input.gpo-file[name="Pet-image"], input#file-1[name="Pet-image"]');
      safeScrollIntoView(gpoFile && (gpoFile.closest('.gpo-element') || gpoFile));
      track('dd_ai_preview_manual_upload_selected', {
        product_handle: module.dataset.productHandle,
        variant_id: getVariantId(module)
      });
    });

    function guardAndAttachForCart(event) {
      if (!module.ddAiPreviewLatestResult && module.ddAiPreviewPendingResult) {
        track('dd_ai_preview_email_skipped', {
          product_handle: module.dataset.productHandle,
          variant_id: getVariantId(module),
          job_id: module.ddAiPreviewPendingResult.job_id || '',
          source: 'cart_submit'
        });
        revealGeneratedPreview(module, module.ddAiPreviewPendingResult, { source: 'cart_submit' });
      }
      const hasPreview = Boolean(module.ddAiPreviewLatestResult);
      syncGpoFieldBypass(module, 0, { photo: hasPreview, hidePhoto: hasPreview, placement: true });
      if (!module.ddAiPreviewLatestResult) {
        module.ddAiPreviewManualUpload = true;
        manualButton.hidden = true;
        syncGpoFieldBypass(module, 0, { photo: false, hidePhoto: false, placement: true });
        status.textContent = 'Upload your pet photo below to continue with your order.';
        track('dd_ai_preview_cart_submit_without_preview', {
          product_handle: module.dataset.productHandle,
          variant_id: getVariantId(module),
          has_preview: false
        });
        return true;
      }
      attachProperties(module, module.ddAiPreviewLatestResult);
      track('dd_ai_preview_cart_submit', {
        product_handle: module.dataset.productHandle,
        variant_id: getVariantId(module),
        has_preview: true
      });
      return true;
    }

    function isAddToCartTarget(event) {
      const target = event.target;
      if (!target || !target.closest) return false;
      const button = target.closest('button[name="add"], input[type="submit"][name="add"], .gpo-clone-atc-button');
      if (!button) return false;

      const form = getProductForm(module);
      const productInfo = module.closest('product-info');
      return Boolean(
        (form && (form.contains(button) || button.form === form)) ||
        (productInfo && productInfo.contains(button)) ||
        button.classList.contains('gpo-clone-atc-button')
      );
    }

    const form = getProductForm(module);
    if (form) {
      form.addEventListener('submit', guardAndAttachForCart, { capture: true });
    }
    document.addEventListener('click', (event) => {
      if (isAddToCartTarget(event)) guardAndAttachForCart(event);
    }, { capture: true });
  }

  function boot() {
    document.querySelectorAll('[data-dd-ai-preview]').forEach((module) => initModule(module));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();